import {
  Currency,
  type AssortmentGroupId,
  type ChannelCostCargoCondition,
  type ChannelCostEstimate_FieldsFragment,
  type ChannelCostEstimateId,
  type ShipId,
} from '@seaber/common/graphql/types';
import { stringToNumOpt } from '../../core/util';
import { formatChannelName } from '../../../../common/core/channels';
import { parseCurrency } from '@seaber/common/lib/currency';
import type { CurrencyRates } from '../../../state/zustand';
import { convertMonetaryValue } from '../../../state/zustand';

/*
 * Types
 */

export type ChannelCostMatchCriteria = {
  channelName: string;
  vesselId: ShipId;
  vesselTypeNames: string[] | null;
  assortmentGroupId: AssortmentGroupId | null;
  cargoCondition: ChannelCostCargoCondition;
};

export type ChannelCostRule = {
  assortmentGroupId: AssortmentGroupId | null;
  assortmentGroupName: string | null;
  cargoCondition: ChannelCostCargoCondition | null;
  channelCost: number;
  channelCostCurrency: Currency;
  channelId: string;
  channelName: string;
  durationOverhead: number | null;
  id: ChannelCostEstimateId;
  vesselId: ShipId | null;
  vesselName: string | null;
  vesselTypeName: string | null;
};

type VesselKey = `vid-${number}` | `vt-${string}` | null;

/**
 * Multi-level index for channel cost lookups. Null acts as catch-all at each level.
 * Levels: channelId -> vessel (vid-N, vt-name, or null) -> assortmentGroupId -> cargoCondition
 */
export type ChannelCostIndex = Map<
  string, // channelId
  Map<
    VesselKey | null, // vid-N, vt-name, or null (catch-all)
    Map<
      AssortmentGroupId | null, // assortmentGroupId or null (catch-all)
      Map<
        ChannelCostCargoCondition | null, // cargoCondition (ballast / laden) or null (catch-all)
        ChannelCostRule
      >
    >
  >
>;

/*
 * Index builder
 */

export function buildChannelCostIndex(
  rules: ChannelCostRule[],
): ChannelCostIndex {
  const index: ChannelCostIndex = new Map();

  for (const rule of rules) {
    const vesselKey: VesselKey | null =
      rule.vesselId !== null
        ? `vid-${rule.vesselId}`
        : rule.vesselTypeName !== null
          ? `vt-${rule.vesselTypeName}`
          : null;

    let byVessel = index.get(rule.channelId);
    if (!byVessel) {
      byVessel = new Map();
      index.set(rule.channelId, byVessel);
    }

    let byAssortment = byVessel.get(vesselKey);
    if (!byAssortment) {
      byAssortment = new Map();
      byVessel.set(vesselKey, byAssortment);
    }

    let byCargo = byAssortment.get(rule.assortmentGroupId);
    if (!byCargo) {
      byCargo = new Map();
      byAssortment.set(rule.assortmentGroupId, byCargo);
    }

    byCargo.set(rule.cargoCondition, rule);
  }

  return index;
}

/*
 * Accessor Functions
 */

/** Returns the best matching channel cost for given criteria */
export function getIndexedChannelCost(
  index: ChannelCostIndex,
  criteria: ChannelCostMatchCriteria,
): ChannelCostRule | null {
  const byVessel = index.get(criteria.channelName);
  if (!byVessel) return null;

  const vesselKeys = getVesselKeys(criteria);
  const assortmentKeys = getAssortmentKeys(criteria);
  const cargoKeys = getCargoKeys(criteria);

  for (const vesselKey of vesselKeys) {
    const byAssortment = byVessel.get(vesselKey);
    if (!byAssortment) continue;

    for (const assortmentKey of assortmentKeys) {
      const byCargo = byAssortment.get(assortmentKey);
      if (!byCargo) continue;

      for (const cargoKey of cargoKeys) {
        const rule = byCargo.get(cargoKey);
        if (rule) return rule;
      }
    }
  }

  return null;
}

/** Returns all potentially matching channel costs for given criteria */
export function getAllIndexedChannelCosts(
  index: ChannelCostIndex,
  criteria: ChannelCostMatchCriteria,
): ChannelCostRule[] {
  const byVessel = index.get(criteria.channelName);
  if (!byVessel) return [];

  const vesselKeys = getVesselKeys(criteria);
  const assortmentKeys = getAssortmentKeys(criteria);
  const cargoKeys = getCargoKeys(criteria);

  const results: ChannelCostRule[] = [];

  for (const vesselKey of vesselKeys) {
    const byAssortment = byVessel.get(vesselKey);
    if (!byAssortment) continue;

    for (const assortmentKey of assortmentKeys) {
      const byCargo = byAssortment.get(assortmentKey);
      if (!byCargo) continue;

      for (const cargoKey of cargoKeys) {
        const rule = byCargo.get(cargoKey);
        if (rule) results.push(rule);
      }
    }
  }

  return results;
}

/*
 * Helper Functions
 */

function getVesselKeys(criteria: ChannelCostMatchCriteria): VesselKey[] {
  const keys: VesselKey[] = [`vid-${criteria.vesselId}`];

  // TODO: remove nullability we can just as well pass empty array
  if (criteria.vesselTypeNames !== null) {
    for (const typeName of criteria.vesselTypeNames) {
      keys.push(`vt-${typeName}`);
    }
  }

  keys.push(null);

  return keys;
}

function getAssortmentKeys(
  criteria: ChannelCostMatchCriteria,
): (AssortmentGroupId | null)[] {
  if (criteria.assortmentGroupId !== null) {
    return [criteria.assortmentGroupId, null];
  }
  return [null];
}

function getCargoKeys(
  criteria: ChannelCostMatchCriteria,
): (ChannelCostCargoCondition | null)[] {
  return [criteria.cargoCondition, null];
}

/*
 * Normalization
 */

export function normalizeChannelCostRules(
  fragments: Array<ChannelCostEstimate_FieldsFragment | null | undefined>,
  currencyRates: CurrencyRates | null,
): ChannelCostRule[] {
  const rules: ChannelCostRule[] = [];

  for (const fragment of fragments) {
    if (!fragment) continue;

    try {
      const normalized = normalizeChannelCostRule(fragment, currencyRates);
      rules.push(normalized);
    } catch (err) {
      console.error('Error normalizing channel cost rule fragment:', err, {
        fragment,
      });
    }
  }

  return rules;
}

export function normalizeChannelCostRule(
  fragment: ChannelCostEstimate_FieldsFragment,
  currencyRates: CurrencyRates | null = null,
): ChannelCostRule {
  const currency = parseCurrency(fragment.channelCostCurrency);

  const convertedChannelCost = convertMonetaryValue(
    stringToNumOpt(fragment.channelCost) ?? 0,
    currency,
    currencyRates,
  );

  return {
    assortmentGroupId: fragment.assortmentGroupId ?? null,
    assortmentGroupName:
      fragment.assortmentGroupByAssortmentGroupId?.name ?? null,
    cargoCondition: fragment.cargoCondition ?? null,
    channelCost: convertedChannelCost ?? 0,
    channelCostCurrency: currencyRates?.baseCurrency ?? Currency.EUR,
    channelId: fragment.channelName,
    channelName: formatChannelName(fragment.channelName),
    durationOverhead: stringToNumOpt(fragment.durationOverhead) ?? null,
    id: fragment.id,
    vesselId: fragment.vesselId ?? null,
    vesselName: fragment.shipByVesselId?.name ?? null,
    vesselTypeName: fragment.vesselAttributeByVesselTypeId?.name ?? null,
  };
}
